import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from "@exhousing/design-system";
import { Pencil } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { useLandlords } from "@/hooks/api/useParties";
import { usePropertyLookups, useUpdateProperty } from "@/hooks/api/useProperties";
import { money } from "@/lib/money";
import {
  type PropertyField,
  type PropertySection,
  isNonNegativeKind,
} from "@/lib/propertyFields";
import type { components } from "@/types/api";

type Property = components["schemas"]["PropertyResponse"];

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

const SELECT =
  "flex h-11 w-full rounded-md border border-input bg-card px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

/** One property detail section, rendered read-only with an inline Edit→Save/Cancel flow.
 *  Saves only its own fields (partial PATCH). Validates non-negative numbers, disables
 *  Save until the form is both changed and valid, and shows each field's data→effect cue
 *  in edit mode. */
export function PropertySectionCard({
  property,
  section,
}: {
  property: Property;
  section: PropertySection;
}) {
  const update = useUpdateProperty(property.id);
  const { data: lookups } = usePropertyLookups();
  const { data: landlords } = useLandlords();
  const [editing, setEditing] = useState(false);

  const initial = () => {
    const init: Record<string, string> = {};
    for (const f of section.fields) {
      const v = (property as Record<string, unknown>)[f.key];
      init[f.key] = v == null ? "" : String(v);
    }
    return init;
  };
  const [form, setForm] = useState<Record<string, string>>(initial);

  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const options = (category: string) =>
    (lookups ?? []).filter((l) => l.category === category);

  const dirty = section.fields.some((f) => form[f.key] !== initial()[f.key]);
  const invalid = section.fields.some((f) => {
    if (!isNonNegativeKind(f.kind)) return false;
    const raw = form[f.key].trim();
    if (raw === "") return false;
    const n = Number(raw);
    return !Number.isFinite(n) || n < 0;
  });

  const cancel = () => {
    setForm(initial());
    setEditing(false);
  };

  const save = (e: React.FormEvent) => {
    e.preventDefault();
    if (invalid) return;
    const body: Record<string, unknown> = {};
    for (const f of section.fields) {
      const v = form[f.key];
      body[f.key] =
        f.kind === "number" || f.kind === "money" ? (v === "" ? null : Number(v)) : v || null;
    }
    update.mutate(body, {
      onSuccess: () => {
        toast.success(`${section.title} updated`);
        setEditing(false);
      },
      onError: () => toast.error(`Could not update ${section.title.toLowerCase()}`),
    });
  };

  // --- Read-mode value resolution ---
  const readValue = (f: PropertyField): string => {
    const raw = (property as Record<string, unknown>)[f.key];
    if (raw == null || raw === "") return "—";
    if (f.kind === "money") return money(Number(raw));
    if (f.kind === "date") return fmtDate(String(raw));
    if (f.kind === "landlord") {
      return (
        (landlords ?? []).find((l) => l.id === raw)?.contact.display_name ?? "—"
      );
    }
    if (f.kind === "select") {
      return (
        options(f.category ?? "").find((o) => o.id === raw)?.label_en ?? String(raw)
      );
    }
    return String(raw);
  };

  const renderInput = (f: PropertyField) => {
    if (f.kind === "landlord") {
      return (
        <select
          id={f.key}
          value={form[f.key] ?? ""}
          onChange={(e) => set(f.key, e.target.value)}
          className={SELECT}
        >
          <option value="">— No landlord —</option>
          {(landlords ?? []).map((l) => (
            <option key={l.id} value={l.id}>
              {l.contact.display_name}
            </option>
          ))}
        </select>
      );
    }
    if (f.kind === "select") {
      return (
        <select
          id={f.key}
          value={form[f.key] ?? ""}
          onChange={(e) => set(f.key, e.target.value)}
          className={SELECT}
        >
          <option value="">—</option>
          {options(f.category ?? "").map((o) => (
            <option key={o.id} value={o.id}>
              {o.label_en}
            </option>
          ))}
        </select>
      );
    }
    if (f.kind === "money") {
      return (
        <div className="relative">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
            €
          </span>
          <Input
            id={f.key}
            type="number"
            min="0"
            step="0.01"
            className="pl-7 tabular-nums"
            value={form[f.key] ?? ""}
            onChange={(e) => set(f.key, e.target.value)}
          />
        </div>
      );
    }
    return (
      <Input
        id={f.key}
        type={f.kind === "number" ? "number" : f.kind === "date" ? "date" : "text"}
        min={f.kind === "number" ? "0" : undefined}
        className={f.kind === "number" ? "tabular-nums" : undefined}
        value={form[f.key] ?? ""}
        onChange={(e) => set(f.key, e.target.value)}
      />
    );
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <section.icon className="size-4 text-muted-foreground" />
          {section.title}
        </CardTitle>
        {!editing && (
          <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
            <Pencil className="size-4" />
            Edit
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {editing ? (
          <form onSubmit={save} className="space-y-4">
            <p className="text-sm text-muted-foreground">{section.description}</p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {section.fields.map((f) => (
                <div key={f.key} className="space-y-1.5">
                  <Label htmlFor={f.key}>{f.label}</Label>
                  {renderInput(f)}
                  {f.impact && (
                    <p className="text-xs text-muted-foreground">{f.impact}</p>
                  )}
                </div>
              ))}
            </div>
            {invalid && (
              <p className="text-xs text-destructive">Amounts can&rsquo;t be negative.</p>
            )}
            <div className="flex gap-2">
              <Button type="submit" size="sm" disabled={update.isPending || invalid || !dirty}>
                {update.isPending ? "Saving…" : "Save"}
              </Button>
              <Button type="button" size="sm" variant="ghost" onClick={cancel}>
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <dl className="grid grid-cols-2 gap-x-5 gap-y-3 sm:grid-cols-3">
            {section.fields.map((f) => (
              <div key={f.key}>
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                  {f.label}
                </dt>
                <dd className="mt-0.5 text-sm tabular-nums">{readValue(f)}</dd>
              </div>
            ))}
          </dl>
        )}
      </CardContent>
    </Card>
  );
}
